import * as fs from "node:fs";
import * as path from "node:path";
import {
  AuditCategory,
  AuditFinding,
  AuditMode,
  AuditReport,
  AuditSeverity,
  DOCS_SYNC_AUDIT_SCHEMA_VERSION,
} from "./audit-report";

/**
 * Default subdirectory beneath the repo root where audit reports are
 * persisted. Mirrors the path declared on issue #518's acceptance
 * criteria: `.state/docs-sync/<N>-audit.json`.
 */
export const DEFAULT_AUDIT_REPORT_DIR = ".state/docs-sync";

/**
 * Severity-ordering rank used when sorting the flat `findings`
 * array. Lower number = higher rank in the report. Keeps blocking
 * issues at the top so consumers reading the array top-to-bottom
 * see the merge-blocking findings first.
 */
const SEVERITY_RANK: Record<AuditSeverity, number> = {
  blocking: 0,
  advisory: 1,
  mechanical: 2,
};

/**
 * Stable iteration order for category keys. Matches the order they
 * appear in the persisted report and the order they ran during the
 * scan (API surface diff first, then TSDoc, then references, then
 * the docs-only checks). The order is part of the contract — a
 * consumer that walks `Object.keys(report.categories)` sees this
 * order on every read.
 */
export const AUDIT_CATEGORY_ORDER: ReadonlyArray<AuditCategory> = [
  AuditCategory.ApiDiff,
  AuditCategory.TsdocCoverage,
  AuditCategory.ReferenceMismatches,
  AuditCategory.LinkFailures,
  AuditCategory.SampleFailures,
];

/**
 * One drift-check runner. The scan orchestrator invokes each
 * runner in registration order, collects the findings, and merges
 * them into the persisted report. Runners are intentionally
 * synchronous to keep the scaffold simple — children #519 and
 * #520 may introduce async runners by adding a parallel
 * `AsyncAuditCheckRunner` shape if needed.
 *
 * Runners are pure functions: given the same context they should
 * produce the same finding list. The scan orchestrator does not
 * sandbox them — a runner that mutates the filesystem or shells
 * out is on its own honor.
 */
export interface AuditCheckRunner {
  /**
   * Stable name used in diagnostics and logs. Conventionally
   * matches the category the runner produces findings in (e.g.
   * `apiDiff`, `tsdocCoverage`).
   */
  readonly name: string;

  /**
   * Compute the findings for this check. Runners must return
   * already-validated finding records — the orchestrator does not
   * post-process category, severity, or location.
   */
  run(context: AuditCheckRunnerContext): ReadonlyArray<AuditFinding>;
}

/**
 * Context passed to every check runner. Carries the same fields
 * the orchestrator needs to populate the report header so runners
 * can consult them when computing their findings (e.g. resolve
 * relative paths against `repoRoot`).
 */
export interface AuditCheckRunnerContext {
  readonly repoRoot: string;
  readonly mode: AuditMode;
  readonly scope: string;
  readonly issueNumber: number;
}

/**
 * Options accepted by {@link runScan}.
 */
export interface RunScanOptions {
  /**
   * Absolute path to the repository root the scan runs against.
   * The audit report's `repoRoot` field is set to this value
   * verbatim — pass an absolute path so consumers reading the
   * report on a different checkout can rewrite paths reliably.
   */
  readonly repoRoot: string;

  /**
   * Tracking-issue number the scan ran for. Determines the
   * persisted filename (`<issueNumber>-audit.json`).
   */
  readonly issueNumber: number;

  /**
   * Invocation mode — `pr` for diff-scoped runs, `audit` for
   * full-repo runs.
   */
  readonly mode: AuditMode;

  /**
   * Optional free-form scope label. Persisted on the report and
   * forwarded to every runner. Defaults to the empty string.
   */
  readonly scope?: string;

  /**
   * Drift-check runners to invoke in order. Empty by default —
   * #518 ships the orchestrator skeleton without any wired
   * checks; #519 and #520 plug them in.
   */
  readonly checks?: ReadonlyArray<AuditCheckRunner>;

  /**
   * Override the timestamp embedded on the report. Used in tests
   * to make output deterministic. Defaults to `new Date()` at
   * scan start.
   */
  readonly now?: Date;

  /**
   * When `true`, persist the report to disk under
   * `<repoRoot>/<reportDir>/<issueNumber>-audit.json`. When
   * `false` (the default), the report is returned in memory only.
   *
   * @default false
   */
  readonly persist?: boolean;

  /**
   * Override the directory the report is written to (relative to
   * {@link RunScanOptions.repoRoot}). Defaults to
   * {@link DEFAULT_AUDIT_REPORT_DIR}.
   *
   * @default ".state/docs-sync"
   */
  readonly reportDir?: string;
}

/**
 * Result returned by {@link runScan}. Always carries the report;
 * `reportPath` is non-empty only when persistence was requested.
 */
export interface RunScanResult {
  /**
   * The report produced by the scan. Schema-compliant whether or
   * not any checks ran — empty `categories` arrays are persisted
   * explicitly.
   */
  readonly report: AuditReport;

  /**
   * Absolute path the report was persisted to. Empty string when
   * `persist` was `false`.
   */
  readonly reportPath: string;
}

/**
 * Run the docs-sync scan phase. Invokes every registered check
 * runner in order, merges the findings into a single
 * schema-compliant {@link AuditReport}, and optionally persists
 * the report to `<repoRoot>/<reportDir>/<issueNumber>-audit.json`.
 *
 * #518 ships this function with an **empty default check list**:
 * invoking `runScan({ repoRoot, issueNumber, mode: "pr" })`
 * produces a report whose `findings` and per-category arrays are
 * all empty. Children #519 and #520 register the real checks by
 * passing them via the `checks` option.
 */
export function runScan(options: RunScanOptions): RunScanResult {
  const repoRoot = path.resolve(options.repoRoot);
  const mode = options.mode;
  const scope = options.scope ?? "";
  const issueNumber = options.issueNumber;
  const checks = options.checks ?? [];
  const now = options.now ?? new Date();
  const persist = options.persist ?? false;
  const reportDir = options.reportDir ?? DEFAULT_AUDIT_REPORT_DIR;

  const context: AuditCheckRunnerContext = {
    repoRoot,
    mode,
    scope,
    issueNumber,
  };

  const collected: Array<AuditFinding> = [];
  for (const check of checks) {
    const result = check.run(context);
    for (const finding of result) {
      collected.push(finding);
    }
  }

  const report = buildReport({
    issueNumber,
    mode,
    scope,
    repoRoot,
    generatedAt: now,
    findings: collected,
  });

  let reportPath = "";
  if (persist) {
    reportPath = persistAuditReport({
      report,
      repoRoot,
      reportDir,
    });
  }

  return { report, reportPath };
}

/**
 * Compute the final {@link AuditReport} from a flat list of
 * findings. Group by category, sort each bucket deterministically,
 * and produce the flat `findings` aggregation in the documented
 * order. Exported so #519 / #520 can build reports incrementally
 * without re-running the whole scan.
 */
export function buildReport(args: {
  readonly issueNumber: number;
  readonly mode: AuditMode;
  readonly scope: string;
  readonly repoRoot: string;
  readonly generatedAt: Date;
  readonly findings: ReadonlyArray<AuditFinding>;
}): AuditReport {
  const buckets = emptyCategoryBuckets();
  for (const finding of args.findings) {
    pushIntoBucket(buckets, finding);
  }

  buckets.apiDiff.sort(compareFindings);
  buckets.tsdocCoverage.sort(compareFindings);
  buckets.referenceMismatches.sort(compareFindings);
  buckets.linkFailures.sort(compareFindings);
  buckets.sampleFailures.sort(compareFindings);

  const flat: Array<AuditFinding> = [];
  for (const key of AUDIT_CATEGORY_ORDER) {
    const bucket = buckets[key] as ReadonlyArray<AuditFinding>;
    for (const finding of bucket) {
      flat.push(finding);
    }
  }
  flat.sort(compareFindings);

  return {
    schemaVersion: DOCS_SYNC_AUDIT_SCHEMA_VERSION,
    issueNumber: args.issueNumber,
    mode: args.mode,
    generatedAt: args.generatedAt.toISOString(),
    repoRoot: args.repoRoot,
    scope: args.scope,
    categories: {
      apiDiff: buckets.apiDiff,
      tsdocCoverage: buckets.tsdocCoverage,
      referenceMismatches: buckets.referenceMismatches,
      linkFailures: buckets.linkFailures,
      sampleFailures: buckets.sampleFailures,
    },
    findings: flat,
  };
}

/**
 * Persist an {@link AuditReport} to disk. The directory is created
 * recursively when missing; the file is written atomically via a
 * temp-file + rename so a partial write never produces a corrupt
 * report.
 */
export function persistAuditReport(args: {
  readonly report: AuditReport;
  readonly repoRoot: string;
  readonly reportDir?: string;
}): string {
  const repoRoot = path.resolve(args.repoRoot);
  const reportDir = args.reportDir ?? DEFAULT_AUDIT_REPORT_DIR;
  const targetDir = path.resolve(repoRoot, reportDir);
  fs.mkdirSync(targetDir, { recursive: true });

  const targetFile = path.join(
    targetDir,
    `${args.report.issueNumber}-audit.json`,
  );
  const tempFile = `${targetFile}.tmp-${process.pid}-${Date.now()}`;

  const serialized = `${JSON.stringify(args.report, null, 2)}\n`;
  fs.writeFileSync(tempFile, serialized, "utf-8");
  fs.renameSync(tempFile, targetFile);
  return targetFile;
}

/**
 * Build an empty {@link AuditReport.categories} record with every
 * key explicitly set to an empty array. Exported so callers that
 * stub the report shape in tests don't have to repeat the keys.
 */
export function emptyCategoryBuckets(): {
  apiDiff: Array<AuditFinding & { category: typeof AuditCategory.ApiDiff }>;
  tsdocCoverage: Array<
    AuditFinding & { category: typeof AuditCategory.TsdocCoverage }
  >;
  referenceMismatches: Array<
    AuditFinding & { category: typeof AuditCategory.ReferenceMismatches }
  >;
  linkFailures: Array<
    AuditFinding & { category: typeof AuditCategory.LinkFailures }
  >;
  sampleFailures: Array<
    AuditFinding & { category: typeof AuditCategory.SampleFailures }
  >;
} {
  return {
    apiDiff: [],
    tsdocCoverage: [],
    referenceMismatches: [],
    linkFailures: [],
    sampleFailures: [],
  };
}

function pushIntoBucket(
  buckets: ReturnType<typeof emptyCategoryBuckets>,
  finding: AuditFinding,
): void {
  switch (finding.category) {
    case AuditCategory.ApiDiff:
      buckets.apiDiff.push(finding);
      return;
    case AuditCategory.TsdocCoverage:
      buckets.tsdocCoverage.push(finding);
      return;
    case AuditCategory.ReferenceMismatches:
      buckets.referenceMismatches.push(finding);
      return;
    case AuditCategory.LinkFailures:
      buckets.linkFailures.push(finding);
      return;
    case AuditCategory.SampleFailures:
      buckets.sampleFailures.push(finding);
      return;
    default: {
      // Exhaustiveness guard — typed against the discriminated
      // union; an unhandled category is a programming error
      // surfaced as a thrown exception rather than silent drop.
      const exhaustive: never = finding;
      throw new Error(
        `runScan: unrecognized finding category: ${JSON.stringify(exhaustive)}`,
      );
    }
  }
}

function compareFindings(a: AuditFinding, b: AuditFinding): number {
  // Severity first so consumers walking the flat findings array
  // top-to-bottom see merge-blocking findings before advisories
  // and mechanical fixes. Category, file, line, and subject act
  // as deterministic tie-breakers.
  const sevDelta = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
  if (sevDelta !== 0) {
    return sevDelta;
  }
  if (a.category !== b.category) {
    return a.category.localeCompare(b.category);
  }
  if (a.location.file !== b.location.file) {
    return a.location.file.localeCompare(b.location.file);
  }
  if (a.location.line !== b.location.line) {
    return a.location.line - b.location.line;
  }
  return a.subject.localeCompare(b.subject);
}
